import React, { useState } from 'react';
import { Typography, Box, Button, Modal, InputAdornment, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useTheme } from '../../contexts/ThemeContext';
import CustomInput from '../Inputs/CustomInput';
import AddIcon from '@mui/icons-material/Add';
import ErrorModal from './ErrorModal';

function isImageURL(url) {
  try {
    const parsed = new URL(url);
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(parsed.pathname);
  } catch (e) {
    return false;
  }
}

const ImagesModal = ({ open, setOpen, images, onSelect }) => {
  const { theme } = useTheme();

  const [input, setInput] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const styles = {
    modal: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '80vw',
      maxWidth: '900px',
      maxHeight: '80vh',
      overflow: 'auto',
      bgcolor: theme.background.primary,
      borderRadius: 2,
      border: `4px solid ${theme.color.primary}`,
      boxShadow: 24,
      p: 4,
      outline: 'none',
    },
    imageContainer: {
      cursor: 'pointer',
      position: 'relative',
      paddingTop: '100%', // 1:1 Aspect ratio
      '&:hover': {
        '& .MuiBox-root': {
          opacity: 0.3,
        },
      },
    },
    image: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: 1,
      border: `1px solid ${theme.border.light}`,
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: theme.color.primary,
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      backgroundColor: theme.accent.success,
      border: `1px solid ${theme.border.light}`
    }
  };

  const handleImageClick = (image) => {
    onSelect(image);
    setOpen(false);
  };

  return <>
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box sx={styles.modal}>
        <Typography
          variant="h6"
          component="h2"
          gutterBottom
          style={{
            fontFamily: theme.fontFamily,
            textAlign: 'center',
            color: theme.text.primary
          }}
        >
          Select a Label Image
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.isArray(images) && images.length > 0 ? <>
            {images.map((image, index) => (
              <Grid item size={3} key={index}>
                <Box
                  sx={styles.imageContainer}
                  onClick={() => handleImageClick(image)}
                >
                  <img
                    src={image}
                    alt={`Selection ${index + 1}`}
                    style={styles.image}
                  />
                  <Box sx={styles.overlay} />
                </Box>
              </Grid>
            ))}
          </> : <>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2 }}>
              <Box sx={{ color: theme.text.primary }}>No images found, Please insert valid image URL</Box>
              <CustomInput
                label="Image URL"
                isError={!input || !isImageURL(input)}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => {
                        if (!input || !isImageURL(input)) {
                          setErrorMessage("Please insert valid image url");
                          setOpenErrorModal(true);
                          return;
                        }
                        handleImageClick(input);
                      }}>
                        <AddIcon sx={{ color: theme.text.primary }} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </>}
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
          <Button
            variant="contained"
            onClick={() => setOpen(false)}
            sx={styles.button}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
    <ErrorModal open={openErrorModal} setOpen={setOpenErrorModal} message={errorMessage} />
  </>;
};

export default ImagesModal;